/**
 * @fileoverview Mock-only in-memory Tenant store.
 * @description Seeds from tenants-seed.json and holds it in a module-level array, in memory only
 * (never localStorage), so changes last for the browser session. This is the canonical bank/tenant
 * record every mock endpoint (tenants, targets, activities) reads and writes through —
 * see tenant.types.ts's Tenant doc comment for why it's one shared entity rather than a
 * sales-only/banking-only copy. Never imported outside this feature's mocks/ folder (targets and
 * activities stores import it directly, since same-feature internal imports are unaffected by
 * ADR-0002's boundary rules).
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { isPendingApproval } from '@/features/sales/lib/prospect-approval.utils'
import { classifyTenantAccess } from '@/features/sales/lib/tenant-access.utils'
import { tierFromBranches } from '@/features/sales/lib/tier.utils'
import { listSalesRoster } from '@/features/sales/mocks/sales-roster-mock-store'

import seedData from './tenants-seed.json'

import type { OwnershipTransfer, Tenant } from '@/features/sales/types/tenant.types'

const tenants: Tenant[] = seedData.map((tenant) => ({
  ...tenant,
  bankType: tenant.bankType as Tenant['bankType'],
  leadRating: tenant.leadRating as Tenant['leadRating'],
  status: tenant.status as Tenant['status'],
  tier: tenant.tier as Tenant['tier'],
  // headOffice is mandatory but wasn't tracked separately from city by the original seed
  // fixtures — city is the closest fallback. The rest are optional for a Prospect, same as a
  // freshly created one that hasn't filled them in yet. The seed gives them only to the clients,
  // whose approval needed them, so editing a client in mock mode isn't blocked on required fields.
  headOffice: tenant.city,
  address: tenant.address ?? '',
  district: tenant.district ?? '',
  pincode: tenant.pincode ?? '',
  website: '',
  employees: null,
  coreBanking: tenant.coreBanking ?? '',
  existingMis: '',
  authorisedContact: { ...tenant.authorisedContact, officePhone: '' },
}))

export interface TenantFilters {
  status?: Tenant['status']
  ownerId?: string
  ownerIds?: string[]
  previousOwnerId?: string
  tier?: Tenant['tier']
  state?: string
  bankType?: Tenant['bankType']
  archived?: boolean
  /** 'approved' (the default) or 'pending' — a prospect awaiting its Sales Head's approval appears
   * in no list unless asked for explicitly, so nothing but the Pending Approval tab shows it. */
  approval?: 'approved' | 'pending'
}

/**
 * @description Lists tenants matching every supplied filter (filters left `undefined` are
 * ignored — an empty string is a supplied value, not an absent one, since Super Admin's
 * viewerUserId is legitimately `''` and must correctly match zero tenants, not every tenant).
 * `ownerIds`, when supplied, is treated as the caller's resolved hierarchy scope (see
 * sales-hierarchy.utils.ts) — a tenant whose `currentOwnerId` is null (not yet assigned) is excluded
 * by an `ownerIds`/`ownerId` filter, since it belongs to no one's scope yet. `previousOwnerId` is the
 * complementary "Transferred Banks" query: tenants this user once owned (appears as a `toOwnerId` in
 * `ownerHistory`) but no longer does — "Previous owners retain read-only access to transferred
 * prospects, but those prospects do not count as their current assignments." Prospects pending
 * approval are left out unless `approval: 'pending'` asks for exactly those; rejected and withdrawn
 * submissions are always left out.
 * @param {TenantFilters} filters - The filters to apply.
 * @returns {Tenant[]} Every matching tenant.
 */
export function listTenants(filters: TenantFilters = {}): Tenant[] {
  return tenants.filter((tenant) => {
    if (filters.status && tenant.status !== filters.status) return false
    // `!== undefined`, never plain truthiness: Super Admin has no sales-roster entry, so
    // viewerUserId (and therefore ownerId/previousOwnerId here) is legitimately `''` for them — a
    // falsy-but-supplied filter that must still match nothing (no tenant has an empty-string
    // owner), not be silently skipped the way `if (filters.ownerId && ...)` would, which would
    // return every tenant unfiltered instead of none.
    if (filters.ownerId !== undefined && tenant.currentOwnerId !== filters.ownerId) return false
    if (
      filters.ownerIds &&
      !(tenant.currentOwnerId && filters.ownerIds.includes(tenant.currentOwnerId))
    ) {
      return false
    }
    if (
      filters.previousOwnerId !== undefined &&
      (tenant.currentOwnerId === filters.previousOwnerId ||
        !tenant.ownerHistory.some((transfer) => transfer.toOwnerId === filters.previousOwnerId))
    ) {
      return false
    }
    if (filters.tier && tenant.tier !== filters.tier) return false
    if (filters.state && tenant.state !== filters.state) return false
    if (filters.bankType && tenant.bankType !== filters.bankType) return false
    if (filters.archived !== undefined && tenant.archived !== filters.archived) return false
    if (tenant.approval === 'Rejected' || tenant.approval === 'Withdrawn') return false
    if (isPendingApproval(tenant) !== (filters.approval === 'pending')) return false
    return true
  })
}

/**
 * @description Lists every tenant, unfiltered — pending, rejected and withdrawn submissions
 * included — for the backend-shaped prospect and approval mocks, which apply the backend's own
 * visibility rules (see sales-crm-mock-projection.ts).
 * @returns {Tenant[]} Every tenant.
 */
export function listEveryTenant(): Tenant[] {
  return [...tenants]
}

/**
 * @description Finds one tenant by id.
 * @param {string} id - The tenant id to look up.
 * @returns {Tenant | undefined} The matching tenant, or undefined.
 */
export function findTenantById(id: string): Tenant | undefined {
  return tenants.find((tenant) => tenant.id === id)
}

export interface CreateTenantInput {
  name: string
  bankType: Tenant['bankType']
  headOffice: string
  regNumber: string
  state: string
  city: string
  address: string
  district: string
  pincode: string
  website: string
  branches: number
  employees: number | null
  coreBanking: string
  existingMis: string
  authorisedContact: Tenant['authorisedContact']
  leadSource: string
  leadRating: Tenant['leadRating']
  notes: string
}

/** Who made a prospect and, when it needs approval, who must give it — see ProspectApproval. */
export type ProspectApprovalDetails = Pick<
  Tenant,
  'approval' | 'createdById' | 'approverId' | 'requestedOwnerId'
>

let nextSequence = tenants.length + 1

/**
 * @description Creates a new tenant record in Prospect status — "Creating a prospect creates its
 * tenant record in Prospect status" (Sales Pipeline). Tier is always derived from branch count,
 * never accepted from the caller.
 * @param {CreateTenantInput} input - The prospect's captured bank-identity fields.
 * @param {ProspectApprovalDetails} [approval] - Its maker and approval state (approved if omitted).
 * @returns {Tenant} The created tenant.
 */
export function createTenant(
  input: CreateTenantInput,
  approval: ProspectApprovalDetails = {},
): Tenant {
  const tenant: Tenant = {
    ...approval,
    id: `TEN-${String(1000 + nextSequence)}`,
    ...input,
    tier: tierFromBranches(input.branches),
    status: 'Prospect',
    archived: false,
    archiveReason: null,
    currentOwnerId: null,
    ownerHistory: [],
    createdAt: new Date().toISOString(),
    clientSince: null,
    onboarding: null,
    subscription: null,
  }
  nextSequence += 1
  tenants.push(tenant)
  return tenant
}

/**
 * @description Marks a pending prospect approved, so it shows up like any other prospect.
 * @param {string} id - The prospect to approve.
 * @returns {Tenant | null} The approved prospect, or null if no prospect with that id is pending.
 */
export function approveTenant(id: string): Tenant | null {
  const tenant = tenants.find((candidate) => candidate.id === id)
  if (!tenant || !isPendingApproval(tenant)) return null
  tenant.approval = 'Approved'
  return tenant
}

export interface UpdateTenantInput {
  name?: string
  bankType?: Tenant['bankType']
  headOffice?: string
  regNumber?: string
  state?: string
  city?: string
  address?: string
  district?: string
  pincode?: string
  website?: string
  branches?: number
  employees?: number | null
  coreBanking?: string
  existingMis?: string
  authorisedContact?: Tenant['authorisedContact']
  leadSource?: string
  leadRating?: Tenant['leadRating']
  notes?: string
}

/**
 * @description Applies an Edit Prospect change straight to a tenant, with no role or scope check
 * (the Sales CRM mock enforces the backend's own edit rules before calling this) and re-derives the
 * tier when the branch count changes.
 * @param {string} id - The tenant id to change.
 * @param {UpdateTenantInput} input - The fields to overwrite.
 * @returns {Tenant | null} The changed tenant, or null when none matched the id.
 */
export function patchTenant(id: string, input: UpdateTenantInput): Tenant | null {
  const tenant = findTenantById(id)
  if (!tenant) return null
  Object.assign(tenant, input)
  if (input.branches !== undefined) {
    tenant.tier = tierFromBranches(input.branches)
  }
  return tenant
}

/**
 * @description Updates a prospect's permitted profile fields. Enforces "Edit Prospect": only
 * before client conversion ("Sales users have read-only access to fact-sheet fields" once
 * `status` is `'Active Client'` — no role exception, including Super Admin), and only for the
 * current owner or a supervisor whose hierarchy scope includes this tenant (the same access rule
 * already enforced for Archive/Restore via classifyTenantAccess, since the spec gives Edit no
 * stricter owner-only language the way it does for sales-stage updates). Branch-count edits
 * re-derive `tier` automatically, same as creation.
 * @param {string} id - The tenant id to update.
 * @param {UpdateTenantInput} input - The fields to change (only supplied fields are touched).
 * @param {string} actingUserId - The editing user's sales-roster id.
 * @param {string} actingUserRole - The editing user's active role name.
 * @returns {Tenant | { error: string } | null} The updated tenant; an error object for a
 * permission failure; or null if no tenant matched the id.
 */
export function updateTenant(
  id: string,
  input: UpdateTenantInput,
  actingUserId: string,
  actingUserRole: string,
): Tenant | { error: string } | null {
  const tenant = findTenantById(id)
  if (!tenant) return null

  if (tenant.status !== 'Prospect') {
    return { error: 'Sales users have read-only access once a bank becomes an Active Client.' }
  }

  const access = classifyTenantAccess(tenant, actingUserId, actingUserRole, listSalesRoster())
  if (!access.isOwner && !access.isInScope) {
    return { error: 'Only the current owner or an authorised supervisor may edit this prospect.' }
  }

  Object.assign(tenant, input)
  if (input.branches !== undefined) {
    tenant.tier = tierFromBranches(input.branches)
  }
  return tenant
}

/**
 * @description Archives a tenant, preserving its record/activities/target history — "Archive is
 * separate from Lost status. Archiving does not cancel the target."
 * @param {string} id - The tenant id to archive.
 * @param {string} reason - The mandatory reason note.
 * @returns {Tenant | null} The updated tenant, or null if no tenant matched the id.
 */
export function archiveTenant(id: string, reason: string): Tenant | null {
  const tenant = findTenantById(id)
  if (!tenant) return null
  tenant.archived = true
  tenant.archiveReason = reason
  return tenant
}

/**
 * @description Restores a previously archived tenant without resetting its history or target
 * dates.
 * @param {string} id - The tenant id to restore.
 * @returns {Tenant | null} The updated tenant, or null if no tenant matched the id.
 */
export function restoreTenant(id: string): Tenant | null {
  const tenant = findTenantById(id)
  if (!tenant) return null
  tenant.archived = false
  tenant.archiveReason = null
  return tenant
}

/**
 * @description Applies an ownership change to a tenant's denormalized `currentOwnerId`/
 * `ownerHistory` mirror — called by targets-mock-store.ts whenever a Target's own ownerId changes,
 * so a tenant-first view never needs to join against Target just to show "who owns this".
 * @param {string} tenantId - The tenant whose ownership changed.
 * @param {OwnershipTransfer} transfer - The transfer to record.
 * @returns {void}
 */
export function recordTenantOwnershipChange(tenantId: string, transfer: OwnershipTransfer): void {
  const tenant = findTenantById(tenantId)
  if (!tenant) return
  tenant.currentOwnerId = transfer.toOwnerId
  tenant.ownerHistory.push(transfer)
}

/**
 * @description Converts a tenant to Active Client status and starts its subscription/onboarding —
 * called by targets-mock-store.ts's confirmPayment, never directly: "Active Client cannot be set
 * through an ordinary manual stage update." Records `clientSince` as the confirmation date the first
 * time only, so a later renewal (which moves `subscription.startedAt`) never changes it.
 * @param {string} tenantId - The tenant to convert.
 * @returns {Tenant | null} The updated tenant, or null if no tenant matched the id.
 */
export function markTenantActiveClient(tenantId: string): Tenant | null {
  const tenant = findTenantById(tenantId)
  if (!tenant) return null
  const startedAt = new Date()
  const renewalDueAt = new Date(startedAt)
  renewalDueAt.setFullYear(renewalDueAt.getFullYear() + 1)
  tenant.status = 'Active Client'
  tenant.clientSince ??= startedAt.toISOString()
  tenant.onboarding = { hrAdminInvited: true, profileComplete: false }
  tenant.subscription = {
    startedAt: startedAt.toISOString(),
    renewalDueAt: renewalDueAt.toISOString(),
  }
  return tenant
}
